/**
 * CreateWallet — Full wallet creation flow.
 *
 * Steps: password entry → confirm → mnemonic display → backup verification
 */

import { useState, useCallback, type ReactNode } from 'react';
import { useWallet } from '../WalletContext.js';
import { passwordStrength } from '../crypto.js';

type Step = 'password' | 'mnemonic' | 'verify';

export function CreateWallet({ onBack }: { onBack: () => void }): ReactNode {
  const { actions } = useWallet();
  const [step, setStep] = useState<Step>('password');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mnemonic, setMnemonic] = useState('');
  const [verifyWords, setVerifyWords] = useState<{ index: number; word: string }[]>([]);
  const [verifyInputs, setVerifyInputs] = useState<Record<number, string>>({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [backedUp, setBackedUp] = useState(false);

  const strength = passwordStrength(password);

  const handleCreatePassword = useCallback(async () => {
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (strength.score < 2) {
      setError('Password is too weak. Add uppercase, numbers, or symbols.');
      return;
    }

    setLoading(true);
    try {
      const result = await actions.createWallet(password);
      setMnemonic(result.mnemonic);

      // Pick 3 random words for verification
      const words = result.mnemonic.split(' ');
      const indices = pickRandomIndices(words.length, 3);
      setVerifyWords(indices.map(i => ({ index: i, word: words[i]! })));
      setStep('mnemonic');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create wallet');
    } finally {
      setLoading(false);
    }
  }, [password, confirmPassword, strength.score, actions]);

  const handleCopyMnemonic = useCallback(() => {
    void navigator.clipboard.writeText(mnemonic);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [mnemonic]);

  const handleProceedToVerify = useCallback(() => {
    if (!backedUp) {
      setError('Please confirm you have backed up your recovery phrase');
      return;
    }
    setError('');
    setStep('verify');
  }, [backedUp]);

  const handleVerify = useCallback(() => {
    setError('');
    for (const { index, word } of verifyWords) {
      if (verifyInputs[index]?.toLowerCase().trim() !== word) {
        setError(`Word #${index + 1} is incorrect. Please check your backup.`);
        return;
      }
    }
    // Verification passed — wallet is already unlocked from the create step
  }, [verifyWords, verifyInputs]);

  // ─── Render ───────────────────────────────────────────────────────────

  if (step === 'password') {
    return (
      <div className="screen">
        <div className="screen-header">
          <button className="btn-ghost" onClick={onBack}>Back</button>
          <h2>Create Wallet</h2>
        </div>
        <div className="screen-body">
          <p className="text-secondary">Choose a strong password to encrypt your wallet.</p>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter password (min 8 characters)"
              autoFocus
            />
            {password.length > 0 && (
              <div className={`strength-meter strength-${strength.score}`}>
                <div className="strength-bar" style={{ width: `${(strength.score / 4) * 100}%` }} />
                <span className="strength-label">{strength.label}</span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              onKeyDown={e => e.key === 'Enter' && void handleCreatePassword()}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            className="btn-primary btn-full"
            onClick={() => void handleCreatePassword()}
            disabled={loading || password.length < 8}
          >
            {loading ? 'Creating...' : 'Create Wallet'}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'mnemonic') {
    const words = mnemonic.split(' ');
    return (
      <div className="screen">
        <div className="screen-header">
          <h2>Recovery Phrase</h2>
        </div>
        <div className="screen-body">
          <p className="text-secondary warning">
            Write down these 12 words in order. This is the ONLY way to recover your wallet.
            Never share them with anyone.
          </p>

          <div className="mnemonic-grid">
            {words.map((word, i) => (
              <div key={i} className="mnemonic-word">
                <span className="mnemonic-index">{i + 1}</span>
                <span className="mnemonic-text">{word}</span>
              </div>
            ))}
          </div>

          <button className="btn-secondary btn-full" onClick={handleCopyMnemonic}>
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={backedUp}
                onChange={e => setBackedUp(e.target.checked)}
              />
              I have written down my recovery phrase and stored it safely
            </label>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            className="btn-primary btn-full"
            onClick={handleProceedToVerify}
            disabled={!backedUp}
          >
            Continue to Verification
          </button>
        </div>
      </div>
    );
  }

  // step === 'verify'
  return (
    <div className="screen">
      <div className="screen-header">
        <h2>Verify Backup</h2>
      </div>
      <div className="screen-body">
        <p className="text-secondary">Enter the following words from your recovery phrase:</p>

        {verifyWords.map(({ index }) => (
          <div key={index} className="form-group">
            <label>Word #{index + 1}</label>
            <input
              type="text"
              value={verifyInputs[index] ?? ''}
              onChange={e => setVerifyInputs(prev => ({ ...prev, [index]: e.target.value }))}
              placeholder={`Enter word #${index + 1}`}
              autoComplete="off"
            />
          </div>
        ))}

        {error && <div className="error-message">{error}</div>}

        <button className="btn-primary btn-full" onClick={handleVerify}>
          Verify & Complete
        </button>
      </div>
    </div>
  );
}

function pickRandomIndices(max: number, count: number): number[] {
  const indices = new Set<number>();
  while (indices.size < count) {
    indices.add(Math.floor(Math.random() * max));
  }
  return Array.from(indices).sort((a, b) => a - b);
}
